import React from 'react';
import { Button, Modal, Space, Tag, Typography } from 'antd';
import { LinkOutlined, PlusOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

const MATCH_TYPE_LABELS: Record<string, string> = {
    email_and_name: 'Email + Name match',
    email: 'Email match',
    name: 'Name match'
};

type PropType = {
    visible: boolean,
    duplicateUsers: Array<any>,
    message?: string,
    selectedUserId?: string,
    onSelectUser: (userId: string) => void,
    onLinkUser: () => void,
    onCreateNewUser: () => void,
    onCancel: () => void,
    isSubmitting?: boolean
}

const getMatchLabel = (matchType?: string) => MATCH_TYPE_LABELS[matchType || ''] || 'Similar user';

const InceptionDuplicateUserModal = ({
    visible,
    duplicateUsers,
    message,
    selectedUserId,
    onSelectUser,
    onLinkUser,
    onCreateNewUser,
    onCancel,
    isSubmitting = false
}: PropType) => {
    return (
        <Modal
            visible={visible}
            footer={null}
            centered
            width={640}
            onCancel={onCancel}
            maskClosable={false}
            zIndex={1300}
        >
            <div>
                <p className="admin-modal-title">Similar Inception User Detected</p>
                <Paragraph style={{ marginBottom: 12 }}>
                    {message || 'A similar user already exists in the Inception server. You can link this user to an existing Inception profile or continue and create a new Inception user.'}
                </Paragraph>
                <div style={{ marginBottom: 16, padding: 12, background: '#f5f8ff', border: '1px solid #d6e4ff', borderRadius: 8 }}>
                    <Text strong>
                        {duplicateUsers.length
                            ? `Select the Inception user to link if you want to reuse an existing profile. ${duplicateUsers.length} possible match${duplicateUsers.length > 1 ? 'es' : ''} found.`
                            : 'A similar user was reported by Inception. You can continue by creating a new Inception user.'}
                    </Text>
                </div>
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    {!duplicateUsers.length && (
                        <div style={{ padding: 16, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
                            <Text type="secondary">No duplicate user details were returned from Inception.</Text>
                        </div>
                    )}
                    {duplicateUsers.map((user: any) => {
                        const isSelected = user?.inceptionUserId === selectedUserId;
                        return (
                            <button
                                key={user?.inceptionUserId}
                                type="button"
                                onClick={() => onSelectUser(user?.inceptionUserId)}
                                style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    background: isSelected ? '#f0f7ff' : '#ffffff',
                                    border: isSelected ? '1px solid #104EA1' : '1px solid #d9d9d9',
                                    borderRadius: 8,
                                    padding: 14,
                                    cursor: 'pointer',
                                    display: 'block',
                                    appearance: 'none',
                                    boxShadow: isSelected ? '0 0 0 3px rgba(16, 78, 161, 0.08)' : 'none',
                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                        <Space wrap>
                                            <Text strong>{user?.name || 'Unnamed user'}</Text>
                                            <Tag color={isSelected ? 'blue' : 'default'}>{getMatchLabel(user?.matchType)}</Tag>
                                        </Space>
                                        <Text type="secondary">{user?.email || 'No email available'}</Text>
                                    </Space>
                                    <Text strong style={{ color: isSelected ? '#104EA1' : '#595959', whiteSpace: 'nowrap' }}>
                                        {isSelected ? 'Selected' : 'Select'}
                                    </Text>
                                </div>
                            </button>
                        );
                    })}
                </Space>
                <div className='admin-modal-btn-container' style={{ marginTop: 24 }}>
                    <Button className='btn btn-bordered' onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Space>
                        <Button
                            className='btn btn-bordered'
                            icon={<LinkOutlined />}
                            onClick={onLinkUser}
                            loading={isSubmitting}
                            disabled={!selectedUserId}
                        >
                            Link User
                        </Button>
                        <Button
                            className='btn btn--primary'
                            icon={<PlusOutlined />}
                            onClick={onCreateNewUser}
                            loading={isSubmitting}
                        >
                            Create New User
                        </Button>
                    </Space>
                </div>
            </div>
        </Modal>
    );
};

export default InceptionDuplicateUserModal;
